/**
 * Tic-Tac-Toe game engine.
 *
 * Board is a 9-cell array (0-8), left-to-right, top-to-bottom:
 *   0 | 1 | 2
 *   --+---+--
 *   3 | 4 | 5
 *   --+---+--
 *   6 | 7 | 8
 *
 * Cells hold "a" (Team A), "b" (Team B), or null (empty).
 */

export type Player = "a" | "b";
export type Cell = Player | null;
export type Board = Cell[];

/** All winning line combinations (rows, columns, diagonals) */
export const WINNING_LINES: readonly number[][] = [
  [0, 1, 2], // top row
  [3, 4, 5], // middle row
  [6, 7, 8], // bottom row
  [0, 3, 6], // left column
  [1, 4, 7], // middle column
  [2, 5, 8], // right column
  [0, 4, 8], // diagonal
  [2, 4, 6], // anti-diagonal
];

export interface TicTacToeState {
  board: Board;
  turn: Player;
  winner: Player | "draw" | null;
  moveCount: number;
}

/** Create a fresh game state */
export function createGame(): TicTacToeState {
  return {
    board: Array(9).fill(null),
    turn: "a",
    winner: null,
    moveCount: 0,
  };
}

/** Validate and apply a move. Returns new state or null if invalid. */
export function applyMove(
  state: TicTacToeState,
  cellIndex: number,
  player: Player,
): TicTacToeState | null {
  // Game already over
  if (state.winner) return null;

  // Not this player's turn
  if (state.turn !== player) return null;

  // Invalid cell
  if (cellIndex < 0 || cellIndex > 8) return null;

  // Cell already taken
  if (state.board[cellIndex] !== null) return null;

  const newBoard = [...state.board];
  newBoard[cellIndex] = player;

  const winner = checkWinner(newBoard);
  const isDraw = !winner && newBoard.every((c) => c !== null);

  return {
    board: newBoard,
    turn: player === "a" ? "b" : "a",
    winner: winner ?? (isDraw ? "draw" : null),
    moveCount: state.moveCount + 1,
  };
}

/** Check if the board has a winner. Returns the winning player or null. */
export function checkWinner(board: Board): Player | null {
  for (const line of WINNING_LINES) {
    const [a, b, c] = line;
    if (board[a] && board[a] === board[b] && board[b] === board[c]) {
      return board[a] as Player;
    }
  }
  return null;
}

/** Encode board to a compact string for Nostr tags */
export function encodeBoard(board: Board): string {
  return board.map((c) => c ?? "_").join("");
}

/** Decode board from compact string */
export function decodeBoard(encoded: string): Board {
  return encoded.slice(0, 9).split("").map((c) => (c === "_" ? null : c as Player));
}

/** Get the winning line cells if there's a winner, null otherwise */
export function getWinningLine(board: Board): number[] | null {
  for (const line of WINNING_LINES) {
    const [a, b, c] = line;
    if (board[a] && board[a] === board[b] && board[b] === board[c]) {
      return line;
    }
  }
  return null;
}